import { DeleteOutline } from "@mui/icons-material";
import {
    Avatar,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItemButton,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton/AcxButton";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import UserContextFieldsEditor from "components/KBo/ClientBrain/UserContextFieldsEditor";
import { observer } from "mobx-react";
import { ClientBrainUserSummary } from "models/ClientBrain/UserContext";
import React, { useState } from "react";
import ClientBrainStore from "stores/ClientBrainStore";
import UserContextStore from "../UserContextStore";
import ClientBrainSectionCard from "../ClientBrainSectionCard";
import { formatShortDate } from "../clientBrainUtils";

type Props = {
    store: ClientBrainStore;
    canEdit?: boolean;
};

function initials(name: string): string {
    return name
        .split(" ")
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

const UserContextDetail: React.FC<{
    userStore: UserContextStore;
    canEdit?: boolean;
}> = observer(({ userStore, canEdit = false }) => {
    const theme = useTheme();
    const ctx = userStore.isEditing ? userStore.draft : userStore.context;

    return (
        <Box sx={{ flex: 1, padding: theme.spacing(3) }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                        sx={{
                            width: 44,
                            height: 44,
                            bgcolor: theme.palette.primary.main,
                        }}
                    >
                        {initials(userStore.displayName)}
                    </Avatar>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: theme.palette.gray[700],
                            }}
                        >
                            {userStore.displayName}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                color: theme.palette.gray[400],
                            }}
                        >
                            {userStore.email}
                        </Typography>
                    </Box>
                </Box>
                {canEdit && userStore.isEditing ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <AcxButton
                            color="white"
                            onClick={() => userStore.cancelEdit()}
                            disabled={userStore.isSaving}
                        >
                            Cancel
                        </AcxButton>
                        <AcxButton
                            color="primary"
                            onClick={() => userStore.save()}
                            loading={userStore.isSaving}
                        >
                            Save
                        </AcxButton>
                    </Box>
                ) : canEdit ? (
                    <AcxButton
                        color="primary"
                        variant="text"
                        onClick={() => userStore.startEdit()}
                    >
                        Edit context
                    </AcxButton>
                ) : null}
            </Box>

            {(userStore.context.modifiedBy || userStore.context.modifiedOn) && (
                <Typography
                    sx={{
                        fontSize: "12px",
                        color: theme.palette.gray[400],
                        marginTop: theme.spacing(1),
                        marginBottom: theme.spacing(2),
                    }}
                >
                    Last updated by {userStore.context.modifiedBy} ·{" "}
                    {formatShortDate(userStore.context.modifiedOn)}
                </Typography>
            )}

            <Divider sx={{ marginBottom: theme.spacing(2.5) }} />

            <UserContextFieldsEditor
                ctx={ctx}
                isEditing={userStore.isEditing}
                onSetRole={(value) => userStore.setRole(value)}
                onSetListFieldText={(field, text) =>
                    userStore.setListFieldText(field, text)
                }
                keyPrefix={userStore.userId}
            />
        </Box>
    );
});

const UserProfilesSection: React.FC<Props> = observer(
    ({ store, canEdit = true }) => {
        const theme = useTheme();
        const [addOpen, setAddOpen] = useState(false);
        const [selectedEligible, setSelectedEligible] =
            useState<ClientBrainUserSummary | null>(null);
        const [adding, setAdding] = useState(false);

        const profiles = store.userProfiles;
        const selected = store.selectedUserStore;

        const confirmAdd = async () => {
            if (!selectedEligible) return;
            setAdding(true);
            try {
                await store.addUserContext(selectedEligible.userId);
                setAddOpen(false);
                setSelectedEligible(null);
            } finally {
                setAdding(false);
            }
        };

        return (
            <ClientBrainSectionCard
                title="User Profiles"
                subtitle="Add context about individual users on the account. KBo adds context layers on top of what users fill in themselves."
                headerAction={
                    canEdit ? (
                        <AcxButton
                            color="primary"
                            variant="text"
                            onClick={() => setAddOpen(true)}
                            disabled={
                                store.isLoading ||
                                store.eligibleUsers.length === 0
                            }
                        >
                            Add context
                        </AcxButton>
                    ) : undefined
                }
            >
                {store.isLoading ? (
                    <Skeleton variant="rounded" height={380} />
                ) : profiles.length === 0 ? (
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: theme.palette.gray[400],
                            fontStyle: "italic",
                            p: 2,
                        }}
                    >
                        No user context yet. Use "Add context" to add a user.
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            border: `1px solid ${theme.palette.gray[200]}`,
                            borderRadius: "6px",
                            overflow: "hidden",
                            minHeight: "380px",
                        }}
                    >
                        <Box
                            sx={{
                                width: "260px",
                                flexShrink: 0,
                                borderRight: `1px solid ${theme.palette.gray[200]}`,
                                backgroundColor: theme.palette.gray[50],
                            }}
                        >
                            <List disablePadding>
                                {profiles.map((profile) => {
                                    const isSelected =
                                        profile.userId === selected?.userId;
                                    return (
                                        <ListItemButton
                                            key={profile.userId}
                                            selected={isSelected}
                                            onClick={() =>
                                                store.setSelectedUser(
                                                    profile.userId,
                                                )
                                            }
                                            sx={{
                                                gap: 1.5,
                                                paddingY: 1.25,
                                                "&.Mui-selected": {
                                                    backgroundColor:
                                                        theme.palette.white
                                                            .main,
                                                },
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: "13px",
                                                    bgcolor:
                                                        theme.palette.primary
                                                            .main,
                                                }}
                                            >
                                                {initials(profile.displayName)}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                        color: theme.palette
                                                            .gray[700],
                                                    }}
                                                >
                                                    {profile.displayName}
                                                </Typography>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontSize: "12px",
                                                        color: theme.palette
                                                            .gray[400],
                                                    }}
                                                >
                                                    {profile.context.role ||
                                                        profile.email}
                                                </Typography>
                                            </Box>
                                            {canEdit && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        store.removeUserContext(
                                                            profile.userId,
                                                        );
                                                    }}
                                                >
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            )}
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Box>

                        {selected && (
                            <UserContextDetail
                                userStore={selected}
                                canEdit={canEdit}
                            />
                        )}
                    </Box>
                )}

                <Dialog
                    open={addOpen}
                    onClose={() => setAddOpen(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>Add user context</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <AcxSelectSingle
                            id="client-brain-add-user"
                            options={store.eligibleUsers}
                            valueField="userId"
                            labelField="email"
                            placeholder="Select a user…"
                            onChange={(user: ClientBrainUserSummary) =>
                                setSelectedEligible(user)
                            }
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <AcxButton
                            color="white"
                            onClick={() => setAddOpen(false)}
                            disabled={adding}
                        >
                            Cancel
                        </AcxButton>
                        <AcxButton
                            color="primary"
                            onClick={confirmAdd}
                            loading={adding}
                            disabled={!selectedEligible}
                        >
                            Add
                        </AcxButton>
                    </DialogActions>
                </Dialog>
            </ClientBrainSectionCard>
        );
    },
);

export default UserProfilesSection;
